import { InformationCircleIcon } from "@heroicons/react/16/solid";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useSidebar } from "../../context/sidebarContext"; // Assuming path is correct

import "swiper/css";
import "swiper/css/navigation";
import BotLogo from "../commons/BotLogo";
import { IconArrowBorderSquare } from "../../assets/icons";
import { Link, useNavigate } from "react-router-dom";
import { useBot } from "../../context/botContext";
import { useState } from "react";
import DrawerExchangeWallet from "../drawers/DrawerExchangeWallet";
import DrawerApiConnect from "../drawers/DrawerApiConnect";
import DialogBotInfo from "../dialogs/DialogBotInfo";
import { normalizeBotName } from "../../utils";

// Bot Card Component for better structure
const BotCard = ({ bot, onActivateBot, onBotInfo, botDescription }) => {
  const botInfo = botDescription[bot.bot_name.toLowerCase()] || {};
  const isComingSoon = ["rebalance", "xeno bot v3"].includes(
    bot.bot_name.toLowerCase()
  );
  const { myExchanges } = useBot();
  const isDisabled = !myExchanges?.length;

  return (
    <div className="bg-white p-4 h-full flex flex-col justify-between rounded-xl border border-[#DDE1E6] hover:border-[#007A78] hover:shadow-custom-dual transition-shadow duration-200 ease-in-out">
      <div>
        <div className="flex items-start">
          <div className="flex gap-2 items-center">
            <BotLogo botName={bot.bot_name} />
            {/* <BotTypeIconDisplay src={botInfo.icon} alt={bot.bot_name} /> */}
            <h3 className="text-lg uppercase font-semibold text-slate-800 mb-0.5">
              {normalizeBotName(bot.bot_name)}
            </h3>
          </div>
        </div>
        <div className="flex-1 mt-3">
          <p className="text-sm text-slate-600 leading-[18px]">
            {botInfo.description}
          </p>
        </div>
      </div>
      <div className="flex flex-col-reverse lg:flex-row items-center lg:items-end justify-between mt-3">
        <button
          className="text-base underline text-[#697077] !p-0 !bg-transparent"
          onClick={() => onBotInfo(bot)}
        >
          Learn more
        </button>

        {isComingSoon ? (
          <button
            disabled
            className="bg-transparent shadow-none text-red-500 py-2 px-5 md:py-2.5 md:px-6 rounded-lg text-sm cursor-not-allowed"
          >
            Coming Soon
          </button>
        ) : (
          <button
            disabled={isDisabled}
            onClick={() => onActivateBot(bot)}
            className="max-lg:w-full max-lg:mb-3  bg-[#007A78] hover:bg-teal-600 text-white py-2 px-5 md:py-2.5 md:px-6 rounded-lg shadow hover:shadow-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-opacity-60 text-sm"
          >
            Activate The Bot
          </button>
        )}
      </div>
    </div>
  );
};

const NewBot = ({ onActivateBot, botLists }) => {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { myExchanges, exchanges, loadingGetExchange } = useBot();

  const isDisabled = !myExchanges?.length;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [openBotInfo, setOpenBotInfo] = useState(false);
  const [selectedBotStrategy, setSelectedBotStrategy] = useState(null);

  const [isApiConnectDrawerOpen, setIsApiConnectDrawerOpen] = useState(false);
  const [selectedWalletForApiDrawer, setSelectedWalletForApiDrawer] =
    useState(null);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleOpenApiConnectDrawer = (wallet) => {
    setSelectedWalletForApiDrawer(wallet); // Set wallet yang akan dikoneksikan
    setIsApiConnectDrawerOpen(true); // Buka drawer API
    if (isDrawerOpen) setIsDrawerOpen(false); // Opsional: tutup drawer list wallet jika terbuka
  };

  // Handler untuk menutup ApiConnectDrawer
  const handleCloseApiConnectDrawer = () => {
    setIsApiConnectDrawerOpen(false);
    setSelectedWalletForApiDrawer(null); // Reset wallet yang dipilih
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleShowBotInfo = (bot) => {
    setSelectedBotStrategy(bot.bot_name.toLowerCase());
    setOpenBotInfo(true);
  };

  const botDescription = {
    xenoarc: {
      description:
        "XENOARC Bot uses the GRID strategy to automate buying and selling at specific margin, capturing profits from market fluctuations 24/7 with precision and efficiency.",
      icon: "/images/icons/icon_bot_xenoarc.svg",
    },
    "xeno bot v2": {
      description:
        "XENOARC Bot uses the GRID strategy to automate buying and selling at specific margin, capturing profits from market fluctuations 24/7 with precision and efficiency.",
      icon: "/images/icons/icon_bot_xenoarc.svg",
    },
    "xeno arc": {
      description:
        "An upgraded Grid strategy with smarter timing. Xenoarc filters trades by waiting for bounce or reversal signals—reducing noise, avoiding bad entries, and boosting precision in volatile markets.",
      icon: "/images/icons/icon_bot_xenoarc.svg",
    },
    "xeno bot v3": {
      description:
        "The next evolution of our GRID strategy bot, offering enhanced features and greater efficiency.",
      icon: "/images/icons/icon_bot_xenoarc.svg",
    },
    grid: {
      description:
        "A classic strategy for profiting from price swings. It places buy and sell orders in a grid to capture gains from small moves—up or down. Best for sideways markets, with steady, stress-free automation.",
      icon: "/images/icons/icon_bot_xenoarc.svg",
    },
    rebalance: {
      description:
        "The Rebalance bot dynamically adjusts your portfolio to maintain target asset ratios, keeping you diversified.",
      icon: "/images/icons/icon_bot_rebalance.svg",
    },
    dca: {
      description:
        "The DCA bot spreads your investment over time with scheduled buys, reducing the impact of market volatility.",
      icon: "/images/icons/icon_bot_dca.svg",
    },
  };

  const renderBotList = () => {
    if (botLists.length === 0) {
      return (
        <div className="text-center text-gray-500 py-10 col-span-full">
          No new bot types available at the moment.
        </div>
      );
    }

    // -----------------------------------------------------
    // SORTING LOGIC ADDED HERE
    // This prioritizes "xenoarc" to be at index 0
    // -----------------------------------------------------
    const sortedBotLists = [...botLists].sort((a, b) => {
      const nameA = a.bot_name.toLowerCase();
      const nameB = b.bot_name.toLowerCase();

      // Check if either bot is xenoarc (handles "xenoarc" or "xeno arc" variations)
      const isXenoA = nameA === "xenoarc" || nameA === "xeno arc";
      const isXenoB = nameB === "xenoarc" || nameB === "xeno arc";

      if (isXenoA && !isXenoB) return -1; // A moves to top
      if (!isXenoA && isXenoB) return 1; // B moves to top
      return 0; // Keep original order for others
    });

    if (isMobile) {
      return (
        <Swiper
          modules={[Navigation]}
          spaceBetween={16}
          slidesPerView={1.2}
          navigation
          className="w-full"
          breakpoints={{
            480: { slidesPerView: 1.5 },
            640: { slidesPerView: 2 },
          }}
        >
          <style>{`
              .swiper-button-next,
              .swiper-button-prev {
                display: none !important;
              }
           `}</style>
          {sortedBotLists.map((bot) => (
            <SwiperSlide key={bot.id} style={{ height: "auto" }}>
              <BotCard
                bot={bot}
                onActivateBot={onActivateBot}
                onBotInfo={handleShowBotInfo}
                botDescription={botDescription}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      );
    }

    return (
      <div className="grid grid-cols-1 overflow-y-auto gap-4 pb-5">
        {sortedBotLists.map((bot) => (
          <BotCard
            key={bot.id}
            bot={bot}
            onActivateBot={onActivateBot}
            onBotInfo={handleShowBotInfo}
            botDescription={botDescription}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="md:h-auto">
      <DrawerExchangeWallet
        loading={loadingGetExchange}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        wallets={exchanges} // Sebaiknya gunakan 'wallets' (state) agar drawer menampilkan data terkini
        onConnectApiClick={handleOpenApiConnectDrawer} // Tambahkan prop ini
      />
      <DrawerApiConnect
        isOpen={isApiConnectDrawerOpen}
        onClose={handleCloseApiConnectDrawer}
        wallet={selectedWalletForApiDrawer}
      />

      <DialogBotInfo
        open={openBotInfo}
        onClose={() => setOpenBotInfo(false)}
        botStrategy={selectedBotStrategy}
      />

      {!myExchanges?.length && !loadingGetExchange && (
        <div className="bg-[#FFCC00]/15 gap-3 p-3 rounded-[10px] border border-[#FFCC00] mb-5 md:mb-6 ">
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="w-full max-w-[18px] h-[18px] text-[#8F7303]" />
            <span className=" text-base text-[#000000]">
              Connect your Exchange Wallet First to Active Trading Bot
            </span>
          </div>

          <span
            onClick={handleOpenDrawer}
            className="text-[#007AFF] underline !mt-1 block cursor-pointer"
          >
            Connect Exchange Wallet
          </span>
        </div>
      )}

      <div className="mb-5 group md:mb-6 bg-black rounded-[10px] p-4 py-5 flex flex-col items-center justify-center relative overflow-hidden ">
        <div className="absolute bottom-[-120px] left-[-92px] w-[520px] h-[160px] bg-[#A962FF] blur-[40px] group-hover:bottom-[-50px] z-0 transition-all duration-500 ease-in-out rounded-[200%]" />

        <div className="relative flex flex-col items-center">
          <div className="flex -space-x-3 mb-3">
            <BotLogo
              iconColor="bg-green-200"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
            <BotLogo
              iconColor="bg-[#FAC482]"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
            <BotLogo
              iconColor="bg-[#B2B7DE]"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
          </div>

          <span className="text-white text-center inline-block leading-[1.2]">
            Still unsure? Run a backtest and experience its potential risk-free!
          </span>

          <button
            className="!p-[1.5px] !px-[2px] rounded-lg gradient-border-1 mt-4"
            onClick={() => navigate("/testing-mode/back-test")}
          >
            <div className="flex items-center gap-2 inner-gradient-border-1 !bg-black/75 !shadow-[0px_1px_0px_0px_rgba(0,0,0,0.05),_0px_4px_4px_0px_rgba(0,0,0,0.05),_0px_10px_10px_0px_rgba(0,0,0,0.10)] !backdrop-blur-[10px] text-sm">
              Take me there{" "}
              <IconArrowBorderSquare className="w-[16px] h-[16px]" />
            </div>
          </button>
        </div>
      </div>

      {renderBotList()}
    </div>
  );
};

export default NewBot;
